import type { APRSeverity, Debt, StrategyName } from '@/types'

export const APR_SEVERITY_THRESHOLDS = {
  critical: 20,
  high: 10,
  medium: 5,
} as const

export function getAPRSeverity(apr: number, isPromo: boolean): APRSeverity {
  if (isPromo) return apr === 0 ? 'zero_promo' : 'promo'
  if (apr >= APR_SEVERITY_THRESHOLDS.critical) return 'critical'
  if (apr >= APR_SEVERITY_THRESHOLDS.high) return 'high'
  if (apr >= APR_SEVERITY_THRESHOLDS.medium) return 'medium'
  return 'low'
}

/**
 * True when the debt has a temporarily lower effective APR from either an
 * active promo rate or an accepted BT offer. Used for severity coloring so
 * "specially priced" debts render in promo/BT colors rather than critical red.
 *
 * Both branches check the date window — an expired promo or BT offer no longer
 * qualifies even if the corresponding object still lingers on the debt record.
 * (Pre-v0.7.5 the promo branch returned true for any present `promo` regardless
 * of expiry; that bug shadowed post-promo coloring in DebtTable + Strategy
 * attack-order rows.)
 */
export function hasActiveSpecialRate(debt: Pick<Debt, 'promo' | 'balanceTransferOffer'>): boolean {
  const now = new Date()
  if (debt.promo && new Date(debt.promo.promoExpiresAt) > now) return true
  const bt = debt.balanceTransferOffer
  return !!(bt && bt.acceptedAt && new Date(bt.offerValidUntil) > now)
}

export const APR_SEVERITY_COLORS: Record<APRSeverity, string> = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#22c55e',
  promo: '#3b82f6',
  zero_promo: '#06b6d4',
}

export const APR_SEVERITY_BG: Record<APRSeverity, string> = {
  critical: 'bg-red-500/10 text-red-400 border-red-500/20',
  high: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  medium: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  low: 'bg-green-500/10 text-green-400 border-green-500/20',
  promo: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  zero_promo: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
}

// Tailwind 400-shade equivalents — chosen for WCAG AA contrast against the
// slate-800/50 strategy-card backgrounds (4.5:1 with the small label text).
// Pre-v0.7.7 these were 500-shades and Lighthouse flagged blue + purple as
// failing 4.42 and 4.11 respectively.
export const STRATEGY_COLORS: Record<StrategyName, string> = {
  avalanche: '#4ade80', // green-400
  snowball: '#60a5fa', // blue-400
  hybrid: '#c084fc', // purple-400
  minimum_only: '#f87171', // red-400
}

export const STRATEGY_LABELS: Record<StrategyName, string> = {
  avalanche: 'Avalanche',
  snowball: 'Snowball',
  hybrid: 'Hybrid',
  minimum_only: 'Minimum Only',
}

export const STRATEGY_DESCRIPTIONS: Record<StrategyName, string> = {
  avalanche: 'Highest APR first — mathematically optimal, saves the most interest',
  snowball: 'Lowest balance first — psychologically motivating, faster early wins',
  hybrid: 'Weighted by APR × balance — balanced approach',
  minimum_only: 'Pay minimums only — baseline danger zone',
}

export const PROMO_ALERT_COLORS = {
  expired: 'bg-red-900/50 border-red-500 text-red-300',
  critical: 'bg-red-900/30 border-red-500/70 text-red-300',
  warning: 'bg-orange-900/30 border-orange-500/70 text-orange-300',
  upcoming: 'bg-blue-900/20 border-blue-500/50 text-blue-300',
}

export const MAX_SIMULATION_MONTHS = 360
export const EXTRA_PAYMENT_MAX = 10000
export const EXTRA_PAYMENT_STEP = 100
